import type { FC, ReactNode } from 'react'
import type { LegalDocument } from '@/i18n/site-legal'

import Link from 'next/link'
import { ArrowRight, CalendarCheck } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { CtaBand, PageHero } from '@/components/site/primitives'
import { useLocale } from '@/i18n/use-locale'
import { siteLegal } from '@/i18n/site-legal'

interface LegalPageProps {
    legal: LegalDocument
    path: string
}

/** Shared layout for the privacy policy and the terms: a sticky table of contents and numbered sections. */
export const LegalPage: FC<LegalPageProps> = ({ legal, path }): ReactNode => {
    const locale = useLocale()
    const copy = siteLegal[locale]

    return (
        <Layout>
            <Seo
                title={legal.seoTitle}
                description={legal.seoDescription}
                path={path}
                breadcrumbs={[{ name: legal.breadcrumb, path }]}
            />

            <PageHero eyebrow={copy.eyebrow} title={legal.title} text={legal.intro}>
                <p className='inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground'>
                    <CalendarCheck className='size-4 text-primary' aria-hidden='true' />
                    {`${copy.updatedLabel} ${copy.updated}`}
                </p>
            </PageHero>

            <section id='document' className='bg-background py-20'>
                <div className='container-page grid gap-12 lg:grid-cols-[260px_1fr] lg:gap-16'>
                    <aside className='lg:sticky lg:top-28 lg:self-start'>
                        <nav aria-label={copy.contentsLabel}>
                            <p className='text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                {copy.contentsLabel}
                            </p>

                            <ol className='mt-4 space-y-1 border-l border-border'>
                                {legal.sections.map((section, index) => (
                                    <li key={section.id}>
                                        <a
                                            href={`#${section.id}`}
                                            className='-ml-px flex gap-3 border-l border-transparent py-1.5 pl-4 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground'
                                        >
                                            <span className='font-mono text-xs leading-5 text-muted-foreground/70'>{String(index + 1).padStart(2, '0')}</span>
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </nav>
                    </aside>

                    <article className='max-w-3xl'>
                        {legal.sections.map((section, index) => (
                            <section key={section.id} id={section.id} className='scroll-mt-28 border-b border-border py-10 first:pt-0 last:border-0'>
                                <h2 className='flex items-baseline gap-4 font-display text-3xl leading-tight sm:text-[2.2rem]'>
                                    <span className='font-mono text-sm text-primary'>{String(index + 1).padStart(2, '0')}</span>
                                    {section.title}
                                </h2>

                                <div className='mt-5 space-y-4 text-[1.02rem] leading-relaxed text-foreground/85'>
                                    {section.paragraphs.map(paragraph => (
                                        <p key={paragraph}>{paragraph}</p>
                                    ))}

                                    {section.list.length > 0 && (
                                        <ul className='space-y-2.5 pl-1'>
                                            {section.list.map(item => (
                                                <li key={item} className='flex gap-3'>
                                                    <span className='mt-[0.6rem] size-1.5 shrink-0 rounded-full bg-primary' aria-hidden='true' />
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {section.after.map(paragraph => (
                                        <p key={paragraph}>{paragraph}</p>
                                    ))}
                                </div>
                            </section>
                        ))}

                        <div className='mt-6 rounded-3xl bg-mist p-7'>
                            <h2 className='text-lg font-semibold'>{copy.contactTitle}</h2>
                            <p className='mt-1.5 leading-relaxed text-muted-foreground'>{copy.contactText}</p>

                            <Link href='/contact' className='group mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary'>
                                {copy.contactLink}
                                <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                            </Link>
                        </div>
                    </article>
                </div>
            </section>

            <CtaBand title={copy.ctaTitle} text={copy.ctaText} />
        </Layout>
    )
}
